import { useEffect, useMemo, useState } from "react";
import { SupplierLogo } from "@/components/GeschaeftspartnerLogo";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { listOrders, type OrderHistoryRow } from "@/lib/orders";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";

export interface MaLinkOrderDialogProps {
  open: boolean;
  maId: number;
  maNummer: string;
  /** Auftrag der MA – Treffer mit gleichem Projekt werden bevorzugt angezeigt. */
  auftragId?: number | null;
  /** Beim Ändern: Order-ID, die nach dem Link getrennt werden soll. */
  replaceOrderId?: number | null;
  /** Bereits dieser MA zugeordnete Bestellungen anzeigen (offene Positionen nachtragen). */
  includeSameMaOrders?: boolean;
  /** Optionaler Hinweis, welche Position verknüpft wird. */
  positionHint?: string | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (orderId: number) => void;
}

function orderLabel(row: OrderHistoryRow): string {
  const bn = (row.bestellnummern || "").trim();
  if (bn) return bn;
  const subject = (row.subject || "").trim();
  if (subject) return subject;
  return `Bestellung #${row.id}`;
}

function projectHint(row: OrderHistoryRow): string | null {
  const projects = row.projects || [];
  if (!projects.length) return null;
  const parts = projects
    .map((p) => {
      const num = (p.auftragsnummer || "").trim();
      if (num) return num;
      const ort = (p.ort || "").trim();
      const desc = (p.beschreibung || "").trim();
      if (ort && desc) return `${ort} · ${desc}`;
      return ort || desc || null;
    })
    .filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

function matchesAuftrag(row: OrderHistoryRow, auftragId: number | null | undefined): boolean {
  if (auftragId == null) return false;
  return (row.projects || []).some((p) => p.auftrag_id === auftragId);
}

export function MaLinkOrderDialog({
  open,
  maId,
  maNummer,
  auftragId = null,
  replaceOrderId = null,
  includeSameMaOrders = false,
  positionHint = null,
  loading = false,
  onClose,
  onConfirm,
}: MaLinkOrderDialogProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [rows, setRows] = useState<OrderHistoryRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setDebouncedQuery("");
    setRows([]);
    setSelectedId(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => setDebouncedQuery(query.trim()), 280);
    return () => window.clearTimeout(t);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSearching(true);
    void (async () => {
      try {
        const result = await listOrders({
          q: debouncedQuery || undefined,
          typo: true,
        });
        if (!cancelled) setRows(result);
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          toast.error(
            err instanceof ApiError ? err.message : "Bestellungen konnten nicht geladen werden.",
          );
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, debouncedQuery]);

  const sortedRows = useMemo(() => {
    const filtered = rows.filter((r) => {
      if (r.source_ma?.id === maId && !includeSameMaOrders) return false;
      if (replaceOrderId != null && r.id === replaceOrderId) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      const aSame = a.source_ma?.id === maId ? 0 : 1;
      const bSame = b.source_ma?.id === maId ? 0 : 1;
      if (includeSameMaOrders && aSame !== bSame) return aSame - bSame;
      const aMatch = matchesAuftrag(a, auftragId) ? 0 : 1;
      const bMatch = matchesAuftrag(b, auftragId) ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return b.id - a.id;
    });
  }, [rows, maId, replaceOrderId, includeSameMaOrders, auftragId]);

  const selected = sortedRows.find((r) => r.id === selectedId) ?? null;
  const reassignMa = selected?.source_ma;

  return (
    <Dialog
      open={open}
      onClose={loading ? () => {} : onClose}
      dirty={selectedId != null}
      size="lg"
      zIndexClass="z-[130]"
      title={
        replaceOrderId != null
          ? "Bestellung ändern"
          : positionHint
            ? "Position mit Bestellung verknüpfen"
            : "Bestellung verknüpfen"
      }
      description={
        replaceOrderId != null
          ? `Andere Bestellung für Materialanforderung ${maNummer} wählen.`
          : positionHint
            ? `Offene Position „${positionHint}“ mit einer bestehenden Bestellung verknüpfen.`
            : `Bestehende Bestellung mit Materialanforderung ${maNummer} verknüpfen. Bereits zugeordnete Bestellungen können offene Positionen nachträglich aufnehmen.`
      }
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={loading} variant="ghost" />
          <Button
            variant="primary"
            loading={loading}
            disabled={selectedId == null || loading}
            onClick={() => {
              if (selectedId != null) onConfirm(selectedId);
            }}
          >
            {replaceOrderId != null ? "Ändern" : "Verknüpfen"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <label className="eh-label" htmlFor="ma-link-order-q">
            Bestellung suchen
          </label>
          <input
            id="ma-link-order-q"
            type="search"
            className="eh-input"
            placeholder="Bestellnummer, Lieferant, Artikel …"
            value={query}
            disabled={loading}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        {reassignMa && reassignMa.id !== maId ? (
          <p className="rounded-ios border border-status-orange/40 bg-status-orange/10 px-3 py-2 text-xs text-label">
            Diese Bestellung ist bereits mit MA{" "}
            <span className="font-semibold">
              {reassignMa.ma_nummer || `#${reassignMa.id}`}
            </span>{" "}
            verknüpft und wird umgehängt.
          </p>
        ) : reassignMa && reassignMa.id === maId ? (
          <p className="rounded-ios border border-accent/30 bg-accent/10 px-3 py-2 text-xs text-label">
            Diese Bestellung ist bereits zugeordnet. Offene Positionen werden
            nachgetragen.
          </p>
        ) : null}

        <div className="max-h-[min(22rem,50vh)] overflow-y-auto rounded-ios border border-separator">
          {searching ? (
            <p className="px-3 py-6 text-center text-sm text-label-secondary">Suche …</p>
          ) : sortedRows.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-label-secondary">
              Keine Bestellungen gefunden.
            </p>
          ) : (
            <ul className="divide-y divide-separator">
              {sortedRows.slice(0, 40).map((row) => {
                const active = row.id === selectedId;
                const sameProject = matchesAuftrag(row, auftragId);
                const otherMa = row.source_ma;
                const proj = projectHint(row);
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      disabled={loading}
                      className={
                        active
                          ? "flex w-full flex-col gap-0.5 bg-accent/10 px-3 py-2.5 text-left"
                          : "flex w-full flex-col gap-0.5 px-3 py-2.5 text-left hover:bg-fill-tertiary"
                      }
                      onClick={() => setSelectedId(row.id)}
                    >
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-medium text-label">
                        {orderLabel(row)}
                        {sameProject ? (
                          <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-normal text-accent">
                            Gleiches Projekt
                          </span>
                        ) : null}
                        {row.cancelled_at ? (
                          <span className="text-[11px] font-normal text-label-tertiary">
                            storniert
                          </span>
                        ) : null}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-label-secondary">
                        <SupplierLogo
                          name={row.supplier_name}
                          partnerId={row.supplier_gp_id}
                          hasLogo={row.supplier_has_logo}
                          logoUpdatedAt={row.supplier_logo_updated_at}
                          className="h-5 w-5 rounded text-[8px]"
                        />
                        <span className="min-w-0 truncate">
                          {[row.supplier_name, proj].filter(Boolean).join(" · ") ||
                            `ID ${row.id}`}
                        </span>
                      </span>
                      {otherMa && otherMa.id === maId ? (
                        <span className="text-[11px] text-accent">
                          Bereits dieser MA zugeordnet
                        </span>
                      ) : otherMa ? (
                        <span className="text-[11px] text-status-orange">
                          Bereits MA {otherMa.ma_nummer || `#${otherMa.id}`}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Dialog>
  );
}
